import React, { useEffect, useState, useCallback } from "react";
import { Container, Row, Col, Card, Badge, Table, Form, Spinner, Pagination, Alert } from "react-bootstrap";
import instance from "../../utils/ApiConfig";

const LIVREUR_SCORE_LABELS = { 1: '👎 Pas satisfait', 2: '😐 Correct', 3: '😍 Excellent' };

const getCategorieVariant = (score) => {
  if (score === null || score === undefined) return 'secondary';
  if (score <= 6) return 'danger';
  if (score <= 8) return 'warning';
  return 'success';
};

const getCategorieLabel = (score) => {
  if (score === null || score === undefined) return '—';
  if (score <= 6) return '😞 Détracteur';
  if (score <= 8) return '😐 Passif';
  return '😍 Promoteur';
};

function NPSListScreen() {
  const [data, setData]         = useState(null);
  const [kpis, setKpis]         = useState(null);
  const [livreurStats, setLivreurStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [page, setPage]     = useState(1);

  const [filters, setFilters] = useState({
    categorie: '',
    periode: '30',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page });
      if (filters.categorie) params.set('categorie', filters.categorie);
      if (filters.periode)   params.set('periode',   filters.periode);

      const res = await instance.get(`/admin/nps?${params}`);
      setData(res.data.data);
      setKpis(res.data.kpis);
      setLivreurStats(res.data.livreurs_stats ?? []);
    } catch (err) {
      setError("Erreur lors du chargement des données NPS.");
    } finally {
      setLoading(false);
    }
  }, [page, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFilter = (key, value) => {
    setPage(1);
    setFilters(f => ({ ...f, [key]: value }));
  };

  const renderNpsScore = (score) => {
    if (score === null) return <span className="text-muted">—</span>;
    return (
      <Badge bg={getCategorieVariant(score)} style={{ fontSize: '14px', padding: '6px 10px' }}>
        {score}/10
      </Badge>
    );
  };

  const renderPagination = () => {
    if (!data || data.last_page <= 1) return null;
    const items = [];
    for (let i = 1; i <= data.last_page; i++) {
      items.push(
        <Pagination.Item key={i} active={i === page} onClick={() => setPage(i)}>{i}</Pagination.Item>
      );
    }
    return <Pagination size="sm" className="mt-3 justify-content-center">{items}</Pagination>;
  };

  return (
    <Container fluid className="py-4">
      <h1 className="h3 mb-4">NPS — Avis clients</h1>

      {/* KPIs */}
      {kpis && (
        <Row className="mb-4 g-3">
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center h-100">
              <Card.Body>
                <div style={{ fontSize: '36px', fontWeight: '800', color: kpis.nps_score >= 0 ? '#43a047' : '#e53935' }}>
                  {kpis.nps_score !== null ? (kpis.nps_score > 0 ? '+' : '') + kpis.nps_score : '—'}
                </div>
                <div className="text-muted small mt-1">Score NPS</div>
                <div className="text-muted" style={{ fontSize: '11px' }}>% promoteurs − % détracteurs</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm text-center h-100">
              <Card.Body>
                <div style={{ fontSize: '28px', fontWeight: '700' }}>{kpis.total_reponses}</div>
                <div className="text-muted small mt-1">Réponses totales</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body>
                <div className="d-flex flex-column gap-1">
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small">😍 Promoteurs</span>
                    <span className="fw-bold text-success">{kpis.pct_promoteurs}% <span className="text-muted fw-normal">({kpis.promoteurs})</span></span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small">😐 Passifs</span>
                    <span className="fw-bold text-warning">{kpis.pct_passifs}% <span className="text-muted fw-normal">({kpis.passifs})</span></span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <span className="small">😞 Détracteurs</span>
                    <span className="fw-bold text-danger">{kpis.pct_detracteurs}% <span className="text-muted fw-normal">({kpis.detracteurs})</span></span>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="d-flex flex-column justify-content-center">
                {/* Barre de progression NPS */}
                <div className="small text-muted mb-2">Répartition</div>
                <div className="d-flex rounded overflow-hidden" style={{ height: '18px' }}>
                  <div style={{ width: `${kpis.pct_detracteurs}%`, backgroundColor: '#e53935' }} title={`Détracteurs ${kpis.pct_detracteurs}%`} />
                  <div style={{ width: `${kpis.pct_passifs}%`,    backgroundColor: '#fb8c00' }} title={`Passifs ${kpis.pct_passifs}%`} />
                  <div style={{ width: `${kpis.pct_promoteurs}%`, backgroundColor: '#43a047' }} title={`Promoteurs ${kpis.pct_promoteurs}%`} />
                </div>
                <div className="d-flex justify-content-between mt-1" style={{ fontSize: '10px', color: '#999' }}>
                  <span>🔴 Détracteurs</span><span>🟡 Passifs</span><span>🟢 Promoteurs</span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Satisfaction livreurs */}
      {livreurStats.length > 0 && (
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <h6 className="fw-semibold mb-3">Satisfaction livreurs</h6>
            <div className="d-flex flex-column gap-3">
              {livreurStats.map((l) => {
                const pctParfait  = l.total > 0 ? Math.round(l.parfait  / l.total * 100) : 0;
                const pctCorrect  = l.total > 0 ? Math.round(l.correct  / l.total * 100) : 0;
                const pctProbleme = l.total > 0 ? Math.round(l.probleme / l.total * 100) : 0;
                return (
                  <div key={l.livreur_id}>
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="fw-semibold" style={{ fontSize: 14, minWidth: 160 }}>{l.nom}</span>
                      <div className="d-flex gap-3 text-muted" style={{ fontSize: 13 }}>
                        <span title="Parfait">👍 {l.parfait}</span>
                        <span title="Correct">😐 {l.correct}</span>
                        <span title="Problème">👎 {l.probleme}</span>
                        <span className="fw-bold" style={{ color: pctParfait >= 75 ? '#43a047' : pctParfait >= 50 ? '#fb8c00' : '#e53935' }}>
                          {l.pct_satisfaction}%
                        </span>
                      </div>
                    </div>
                    <div className="d-flex rounded overflow-hidden" style={{ height: 10 }}>
                      {pctParfait  > 0 && <div style={{ width: `${pctParfait}%`,  backgroundColor: '#43a047' }} title={`👍 ${pctParfait}%`} />}
                      {pctCorrect  > 0 && <div style={{ width: `${pctCorrect}%`,  backgroundColor: '#fb8c00' }} title={`😐 ${pctCorrect}%`} />}
                      {pctProbleme > 0 && <div style={{ width: `${pctProbleme}%`, backgroundColor: '#e53935' }} title={`👎 ${pctProbleme}%`} />}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Filtres */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={3}>
              <Form.Label className="small fw-semibold">Catégorie</Form.Label>
              <Form.Select size="sm" value={filters.categorie} onChange={e => handleFilter('categorie', e.target.value)}>
                <option value="">Toutes</option>
                <option value="promoteur">😍 Promoteurs (9-10)</option>
                <option value="passif">😐 Passifs (7-8)</option>
                <option value="detracteur">😞 Détracteurs (0-6)</option>
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="small fw-semibold">Période</Form.Label>
              <Form.Select size="sm" value={filters.periode} onChange={e => handleFilter('periode', e.target.value)}>
                <option value="7">7 derniers jours</option>
                <option value="30">30 derniers jours</option>
                <option value="90">90 derniers jours</option>
                <option value="365">12 derniers mois</option>
                <option value="">Tout</option>
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tableau */}
      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0" style={{ fontSize: '14px' }}>
                <thead className="table-light">
                  <tr>
                    <th>Date</th>
                    <th>Commande</th>
                    <th>Client</th>
                    <th>Score</th>
                    <th>Catégorie</th>
                    <th>Livreur</th>
                    <th>Note livraison</th>
                    <th style={{ minWidth: '200px' }}>Réponse contextuelle</th>
                    <th style={{ minWidth: '200px' }}>Commentaire</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data?.length === 0 ? (
                    <tr><td colSpan={9} className="text-center text-muted py-4">Aucun avis pour cette période.</td></tr>
                  ) : (
                    data?.data?.map(r => (
                      <tr key={r.id}>
                        <td className="text-nowrap">
                          {r.responded_at
                            ? new Date(r.responded_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                            : '—'}
                        </td>
                        <td>
                          <a href={`/commande-fournisseur/show/${r.commande_fournisseur_id}`} className="fw-semibold">
                            #{r.commande_fournisseur_id}
                          </a>
                        </td>
                        <td className="small">
                          {r.commande_fournisseur && (
                            <div className="fw-semibold">{r.commande_fournisseur.PrenomClient} {r.commande_fournisseur.NomClient}</div>
                          )}
                          <div className="text-muted">{r.client_email}</div>
                        </td>
                        <td>{renderNpsScore(r.score)}</td>
                        <td>
                          <Badge bg={getCategorieVariant(r.score)} className="fw-normal">
                            {getCategorieLabel(r.score)}
                          </Badge>
                          {/* Note 9-10 d'un client de négoce, transmise au négoce par le
                              récap du soir (nps:notifier-negoces). */}
                          {r.negoce_notifie_le && (
                            <div className="text-muted small mt-1">
                              transmis au négoce le{' '}
                              {new Date(r.negoce_notifie_le).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                            </div>
                          )}
                        </td>
                        <td className="text-muted small">
                          {r.livreur
                            ? `${r.livreur.prenom ?? ''} ${r.livreur.nom ?? ''}`.trim()
                            : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          {r.livreur_score
                            ? LIVREUR_SCORE_LABELS[r.livreur_score]
                            : <span className="text-muted">—</span>}
                        </td>
                        <td className="text-muted small" style={{ maxWidth: '220px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {r.contextual_answer || <span className="text-muted">—</span>}
                        </td>
                        <td className="text-muted small" style={{ maxWidth: '220px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {r.feedback_text || <span className="text-muted">—</span>}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {renderPagination()}
          {data && (
            <p className="text-muted small text-center mt-2">
              {data.total} réponse{data.total > 1 ? 's' : ''} — page {data.current_page}/{data.last_page}
            </p>
          )}
        </>
      )}
    </Container>
  );
}

export default NPSListScreen;
